import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Shift, ShiftEmployee, ShiftFormEmployee, TimezoneResponse } from '../models/shift.model';
import { environment } from 'src/environments/environment';

export interface ShiftResponse {
  success: boolean;
  shifts: Shift[];
}

export interface CreateShiftResponse {
  success: boolean;
  shift_id: number;
  company_id: number;
  shift_name: string;
  assigned_rows: number;
  is_overnight: boolean;
  timezone: string;
  shift_start_time: string;
  shift_end_time: string;
  buffer_start_minutes?: number;
  buffer_end_minutes?: number;
  strict_window?: boolean;
  employee_ids?: number[];
  employees?: ShiftEmployee[];
}

export interface ShiftPayload {
  shift_name: string;
  timezone: string;
  shift_start_time: string;
  shift_end_time: string;
  working_days: number[];
  is_overnight: boolean;
  buffer_start_minutes?: number;
  buffer_end_minutes?: number;
  strict_window?: boolean;
  employee_ids?: number[];
}

export interface ApiError {
  success: false;
  code: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ShiftService {
  private readonly baseUrl = `${environment.api.baseUrl}supersee/v1/shifts`;

  constructor(private http: HttpClient) { }

  getShifts(companyId: number | string, query: string = ''): Observable<Shift[]> {
    return this.http.get<any>(`${this.baseUrl}/${companyId}${query}`).pipe(
      map(response => {
        const shifts = response?.shifts ?? response?.data?.shifts ?? [];
        if (response?.success && Array.isArray(shifts)) {
          return shifts
            .map(s => this.normalizeShift(s))
            .filter(s => s.status !== 'inactive' && s.is_active !== false);
        }
        throw new Error('Failed to fetch shifts');
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Resolve the shift assigned to an employee from an already-loaded company shift list.
   * Priority: shift.employee_ids membership (Shift Management source of truth)
   *         → preferredShiftId (user meta) only when not found in employee_ids
   *         → null (no company-default fallback)
   */
  resolveEmployeeShift(
    shifts: Shift[],
    employeeId: number | string | null | undefined,
    preferredShiftId?: number | string | null
  ): Shift | null {
    if (!Array.isArray(shifts) || shifts.length === 0) {
      return null;
    }

    const eid = employeeId != null && String(employeeId).trim() !== '' ? Number(employeeId) : NaN;
    if (!Number.isNaN(eid)) {
      const assigned = shifts.find(
        (s) => Array.isArray(s?.employee_ids) && s.employee_ids.some((id) => Number(id) === eid)
      );
      if (assigned) {
        return assigned;
      }
    }

    const preferredId = this.normalizeShiftId(preferredShiftId);
    if (preferredId != null) {
      const preferred = shifts.find((s) => Number(s?.id) === preferredId);
      if (preferred) {
        return preferred;
      }
    }

    return null;
  }

  /** Unwrap WP meta arrays / string ids into a numeric shift id. */
  private normalizeShiftId(raw: number | string | null | undefined | unknown): number | null {
    if (raw == null) {
      return null;
    }
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const id = Number(value);
    return Number.isFinite(id) ? id : null;
  }

  getShiftEmployees(shiftId: number): Observable<ShiftFormEmployee[]> {
    return this.http.get<any>(`${this.baseUrl}/${shiftId}/employees`).pipe(
      map(response => {
        const employees = response?.employees ?? response?.data?.employees ?? [];
        if (response?.success && Array.isArray(employees)) {
          return employees;
        }
        throw new Error('Failed to fetch shift employees');
      }),
      catchError(this.handleError)
    );
  }

  updateShiftEmployees(shiftId: number, employeeIds: number[]): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${shiftId}/employees`, { employee_ids: employeeIds }).pipe(
      catchError(this.handleError)
    );
  }

  deleteShift(shiftId: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${shiftId}`).pipe(
      catchError(this.handleError)
    );
  }

  createShift(payload: ShiftPayload): Observable<CreateShiftResponse> {
    return this.http.post<CreateShiftResponse>(this.baseUrl, payload).pipe(
      map(response => {
        if (response.success) {
          return response;
        }
        throw new Error('Failed to create shift');
      }),
      catchError(this.handleError)
    );
  }

  getTimezones(): Observable<TimezoneResponse> {
    const timezoneUrl = `${environment.api.baseUrl}supersee/v1/configs/timezones`;
    return this.http.get<TimezoneResponse>(timezoneUrl).pipe(
      map(response => {
        if (response.success) {
          return response;
        }
        throw new Error('Failed to fetch timezones');
      }),
      catchError(this.handleError)
    );
  }

  updateShift(shiftId: number, payload: Partial<ShiftPayload>): Observable<Shift> {
    return this.http.put<any>(`${this.baseUrl}/${shiftId}`, payload).pipe(
      map(response => {
        if (response.success) {
          return this.normalizeShift(response?.shift ?? response?.data?.shift ?? response);
        }
        throw new Error('Failed to update shift');
      }),
      catchError(this.handleError)
    );
  }

  private normalizeShift(raw: any): Shift {
    return {
      id: Number(raw?.id ?? raw?.shift_id),
      company_id: Number(raw?.company_id),
      shift_name: raw?.shift_name ?? '',
      status: raw?.status ?? 'active',
      timezone: raw?.timezone ?? 'Asia/Kolkata',
      start_time: raw?.start_time ?? raw?.shift_start_time ?? '00:00:00',
      end_time: raw?.end_time ?? raw?.shift_end_time ?? '00:00:00',
      working_days: Array.isArray(raw?.working_days) ? raw.working_days : [],
      is_overnight: !!raw?.is_overnight,
      buffer_start_minutes: raw?.buffer_start_minutes ?? 0,
      buffer_end_minutes: raw?.buffer_end_minutes ?? 0,
      strict_window: raw?.strict_window ?? false,
      is_active: raw?.is_active !== false,
      employee_ids: Array.isArray(raw?.employee_ids)
        ? raw.employee_ids.map(Number)
        : Array.isArray(raw?.employees)
          ? raw.employees.map((e: any) => Number(e?.id ?? e?.user_id)).filter((id: number) => Number.isFinite(id))
          : [],
      employee_count: raw?.employee_count ?? raw?.employee_ids?.length ?? 0,
      employees: raw?.employees ?? [],
      created_at: raw?.created_at,
      updated_at: raw?.updated_at,
    };
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';

    if (error.status === 409) {
      const apiError: ApiError = error.error;
      if (apiError?.code === 'shift_limit_reached') {
        errorMessage = 'Only one shift is allowed currently.';
      } else {
        errorMessage = apiError?.message || 'Shift creation failed';
      }
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(() => new Error(errorMessage));
  }
}
